import { postRequest, getRequest, deleteRequest, putRequest, postRequestWithHeaders } from "../api/apiService";

/**
 * Creates a new location by sending a POST request to the server.
 * 
 * This function constructs a payload with the location's name and description, then sends
 * the data to the server using the `postRequest` function. If the request is successful, it logs
 * the response and returns a success object. If an error occurs, it logs the error and returns a failure object.
 * 
 * @param {string} name - The name of the location to be created.
 * @param {string} description - A description of the location.
 * 
 * @returns {Promise<Object>} An object indicating the success or failure of the operation, along with the response data or error.
 */
export const createLocation = async (nameOrData, description) => {
  let payload;
  if (typeof nameOrData === 'object' && nameOrData !== null) {
    payload = nameOrData;
  } else {
    payload = { name: nameOrData, description };
  }

  try {
    const response = await postRequest("/api/locations", payload);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Fetches all locations from the server.
 * 
 * This function sends a GET request to the server to retrieve all location data.
 * If the request is successful, it logs the response and returns the list of locations.
 * If an error occurs, it logs the error and throws it for further handling.
 * 
 * @returns {Promise<Array>} A promise that resolves with an array of all locations.
 * 
 * @throws {Error} Throws an error if the GET request fails.
 */
export const fetchAllLocations = async () => {
  try {
    // Send a GET request to fetch all locations
    const response = await getRequest("/api/locations");
    return response.data; // Assuming the response contains the locations in the 'data' field
  } catch (error) {
    throw error; // Rethrow the error for further handling
  }
};


/**
 * Fetches location details by ID from the API.
 *
 * @param {string | number} locationId - The ID of the location to retrieve.
 * @returns {Promise<Object>} A promise that resolves to the location details.
 * @throws {Error} Throws an error if the request fails.
 */
export const fetchLocationDetailsById = async (locationId) => {
  try {
    const response = await getRequest(`/api/locations/${locationId}`);
    return response;
  } catch (error) {
    throw error;
  }
};


/**
 * Deletes a location by its ID.
 * Makes an asynchronous DELETE request to the server to remove the location.
 * 
 * @param {string} assetId - The ID of the location to be deleted.
 * @returns {Promise} - The response from the DELETE request.
 * @throws {Error} - Throws an error if the delete request fails.
 */
export const deleteLocationById = async (assetId) => {
  try {
    const response = await deleteRequest(`/api/locations/${assetId}`);
    return response;
  } catch (error) {
    throw error;
  }
};


/**
 * Assigns a child location to a parent location by making a PUT request to the API.
 * 
 * This function sends a request to update the hierarchy of locations, linking 
 * the specified child location to a parent location in the system.
 * 
 * @param {string | number} locationChildId - The ID of the child location to be assigned.
 * @param {string | number} locationParentId - The ID of the parent location to which the child location will be assigned.
 * @returns {Promise<Object>} - The response from the API if the request is successful.
 * @throws {Error} - Throws an error if the request fails.
 */
export const assignLocationToparent = async (locationChildId, locationParentId) => {
  try {
    const response = await putRequest(`/api/locations/${locationChildId}/assign-location/${locationParentId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Uploads multiple image files to the server for a given location.
 *
 * @param {Number} locationId - The ID of the location to associate the images with.
 * @param {File[]} files - The list of image files to upload.
 * @returns {Object} - { success: Boolean, data: Object | error: Object }
 */
export const uploadLocationImage = async (locationId, files) => {
  const formData = new FormData();
  // Append multiple files
  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  try {
    // Note: Assuming API endpoint pattern matches AssetController: /api/locations/upload/{id}
    // Checking LocationController found: @PostMapping("/upload/{locationId}")
    const response = await postRequestWithHeaders(`/api/locations/upload/${locationId}`, formData, {});

    if (response.status !== 200) {
      throw new Error(response.statusText || "Image upload failed");
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error while uploading location image:", error);
    return { success: false, error };
  }
};

/**
 * Sets an image as the primary image for a location.
 * @param {string} imageId - ID of the image to set as primary.
 */
export const setPrimaryLocationImage = async (imageId) => {
  try {
    const response = await putRequest(`/api/locations/images/${imageId}/primary`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Deletes a location image by its ID.
 * @param {string} imageId - ID of the image to delete.
 */
export const deleteLocationImage = async (imageId) => {
  try {
    const response = await deleteRequest(`/api/locations/images/${imageId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Updates an existing location.
 * @param {string} locationId - The ID of the location to update.
 * @param {Object} locationData - The updated location data.
 * @returns {Promise<Object>} - A promise that resolves to the updated location.
 */
export const updateLocation = async (locationId, locationData) => {
  try {
    const response = await putRequest(`/api/locations/${locationId}`, locationData);
    return response.data;
  } catch (error) {
    throw error;
  }
};